import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid2,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { submitReport } from '../../../../helpers/api';
import {
  AssessmentOutlined,
  Circle,
  EventNoteOutlined,
  PanoramaFishEye,
  CheckCircle,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import { reportStatuses, reportTypes, userCats } from '../../../../helpers/appstate';
import { Link } from 'react-router-dom';
import RejectResult from '../result-action/RejectResult';
import ApproveResult from '../result-action/ApproveResult';
import SubmitResult from '../result-action/SubmitResult';
import ResultComments from '../result-action/ResultComments';
import ResultViewMeasurementsParameterInput from './ResultViewMeasurementsParameterInput';
import ResultViewMeasurementsParameterView from './ResultViewMeasurementsParameterView';
import FinalizeResult from '../result-action/FinalizeResult';

export default function ResultViewMeasurements({ props, report, onRefresh }) {
  const { userCategory, userPermission, userId } = props;
  const userPerm = userPermission?.map((mp) => mp.trim().toLowerCase()) || [];
  const canPublishResult = userPerm.includes('publish_report');

  const [selectedSamplePoint, setSelectedSamplePoint] = useState();
  const [submitionDialog, setSubmitionDialog] = useState(false);
  const [openPublish, setOpenPublish] = useState(false);
  const [openReject, setOpenReject] = useState(false);
  const [openComment, setOpenComment] = useState(false);
  const [openFinalize, setOpenFinalize] = useState(false);

  useEffect(() => {
    if (!selectedSamplePoint && report?.samplePoints?.length > 0) {
      setSelectedSamplePoint(report?.samplePoints[0]);
    }
  }, [report, selectedSamplePoint]);

  const getProgressColor = () => {
    const p = report?.resultProgressPercentage || 0;
    if (p >= 100) return 'success';
    if (p >= 50) return 'primary';
    if (p > 0) return 'warning';
    return 'error';
  };

  const renderRemarkButton = () => {
    if (report?.reportComments?.length > 0) {
      return (
        <Button
          variant="text"
          size="small"
          sx={{ textTransform: 'none', fontWeight: 700 }}
          onClick={() => setOpenComment(true)}
        >
          View Comments ({report.reportComments.length})
        </Button>
      );
    }
    return null;
  };

  const renderSamplePoints = () => {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 2,
          mb: 4,
        }}
      >
        {report?.samplePoints?.map((item, index) => {
          const isSelected = item?.samplePointId === selectedSamplePoint?.samplePointId;
          const checkCounts = Array.from({ length: item?.checkCount || 0 }, (_, i) => i + 1);
          const results = Array.from({ length: item?.resultCount || 0 }, (_, i) => i + 1);

          return (
            <Card
              key={index}
              elevation={0}
              sx={{
                borderRadius: '12px',
                border: '2px solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                transition: 'all 0.2s ease-in-out',
                bgcolor: isSelected ? alpha('#1976d2', 0.05) : '#fff',
                '&:hover': { borderColor: 'primary.light', transform: 'translateY(-2px)' },
              }}
            >
              <CardActionArea onClick={() => setSelectedSamplePoint(item)} sx={{ p: 1 }}>
                <CardContent sx={{ p: 1 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{ mb: 2 }}
                  >
                    <Box
                      sx={{
                        p: 0.8,
                        borderRadius: '8px',
                        bgcolor: isSelected ? 'primary.main' : alpha('#000', 0.04),
                        color: isSelected ? '#fff' : 'text.secondary',
                      }}
                    >
                      {item?.icon ? (
                        <img
                          src={item.icon}
                          style={{ width: 24, height: 24, objectFit: 'contain' }}
                          alt=""
                        />
                      ) : (
                        <AssessmentOutlined sx={{ fontSize: 20 }} />
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.3}>
                      {checkCounts.map((c) =>
                        results.includes(c) ? (
                          <CheckCircle key={c} sx={{ fontSize: 14, color: 'success.main' }} />
                        ) : (
                          <RadioButtonUnchecked
                            key={c}
                            sx={{ fontSize: 14, color: 'text.disabled' }}
                          />
                        )
                      )}
                    </Stack>
                  </Stack>

                  <Typography
                    variant="body2"
                    fontWeight={700}
                    noWrap
                    sx={{ color: isSelected ? 'primary.main' : 'text.primary' }}
                  >
                    {item?.samplePointName}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={500}
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    {item?.measurementName}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      {/* HEADER INFO CARD */}
      <Paper
        elevation={0}
        sx={{ p: 2.5, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}
      >
        <Grid2 container spacing={3} alignItems="center">
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  bgcolor: alpha('#1976d2', 0.1),
                  color: 'primary.main',
                  display: 'flex',
                }}
              >
                <AssessmentOutlined />
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight={700}>
                  FACILITY
                </Typography>
                <Link
                  to={`/facilities/${report?.facilityId}/view`}
                  style={{ textDecoration: 'none' }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="text.primary"
                    sx={{ '&:hover': { color: 'primary.main' } }}
                  >
                    {report?.facility}
                  </Typography>
                </Link>
              </Box>
            </Stack>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  bgcolor: alpha('#9c27b0', 0.1),
                  color: '#9c27b0',
                  display: 'flex',
                }}
              >
                <EventNoteOutlined />
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight={700}>
                  PERIOD
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  Q{report?.reportQuarter}, {report?.reportYear}
                </Typography>
              </Box>
            </Stack>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight={700} color={getProgressColor()}>
                  COMPLETION PROGRESS
                </Typography>
                <Typography variant="caption" fontWeight={700} color={getProgressColor()}>
                  {Math.round(report?.resultProgressPercentage || 0)}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.round(report?.resultProgressPercentage || 0)}
                color={getProgressColor()}
                sx={{ height: 8, borderRadius: 5, bgcolor: alpha('#000', 0.05) }}
              />
            </Box>
          </Grid2>
        </Grid2>
      </Paper>

      {/* ACTION ALERTS */}
      <Stack spacing={2} sx={{ mb: 3 }}>
        {report?.reportStatus === reportStatuses.PUBLISHED && (
          <Alert
            severity="success"
            icon={<CheckCircle />}
            sx={{ borderRadius: '12px', fontWeight: 600 }}
            action={
              <Stack direction="row" spacing={1}>
                {renderRemarkButton()}
              </Stack>
            }
          >
            This result has been published and is now locked for editing.
          </Alert>
        )}

        {report?.reportStatus === reportStatuses.SUBMITTED && (
          <Alert
            severity="info"
            sx={{ borderRadius: '12px', alignItems: 'center' }}
            action={
              <Stack direction="row" spacing={1}>
                {renderRemarkButton()}
                {canPublishResult && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={() => setOpenPublish(true)}
                      sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => setOpenReject(true)}
                      sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                      Reject
                    </Button>
                  </>
                )}
              </Stack>
            }
          >
            Result submitted and awaiting administrative review.
          </Alert>
        )}

        {report?.submissionReady && report?.reportStatus === reportStatuses.DRAFT && (
          <Alert
            severity="warning"
            sx={{ borderRadius: '12px', alignItems: 'center' }}
            action={
              <Stack direction="row" spacing={1}>
                {renderRemarkButton()}
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  onClick={() =>
                    userCategory === userCats.CONSULTANT
                      ? setOpenFinalize(true)
                      : setSubmitionDialog(true)
                  }
                  sx={{ textTransform: 'none', borderRadius: '8px' }}
                >
                  {userCategory === userCats.CONSULTANT ? 'Finalize' : 'Submit'}
                </Button>
              </Stack>
            }
          >
            Data collection complete. Please{' '}
            {userCategory === userCats.CONSULTANT ? 'finalize' : 'submit'} the results.
          </Alert>
        )}

        {report?.reportStatus === reportStatuses.REJECTED && (
          <Alert
            severity="error"
            sx={{ borderRadius: '12px', alignItems: 'center' }}
            action={
              <Stack direction="row" spacing={1}>
                {renderRemarkButton()}
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  onClick={() => setSubmitionDialog(true)}
                  sx={{ textTransform: 'none', borderRadius: '8px' }}
                >
                  Resubmit
                </Button>
              </Stack>
            }
          >
            Submission rejected. Please address the comments and resubmit.
          </Alert>
        )}
      </Stack>

      {/* SAMPLE POINTS SELECTION */}
      {renderSamplePoints()}

      {/* PARAMETER INPUT/VIEW SECTION */}
      <Box sx={{ mt: 2 }}>
        {userCategory === userCats.EDM &&
          (report?.reportType === reportTypes.QAQC ? (
            <ResultViewMeasurementsParameterInput
              report={report}
              samplePoint={selectedSamplePoint}
              onRefresh={(sp) => {
                setSelectedSamplePoint(sp);
                onRefresh();
              }}
            />
          ) : (
            <ResultViewMeasurementsParameterView
              report={report}
              samplePoint={selectedSamplePoint}
              onRefresh={(sp) => {
                setSelectedSamplePoint(sp);
                onRefresh();
              }}
            />
          ))}

        {userCategory === userCats.CONSULTANT &&
          (report?.reportType === reportTypes.NORMAL ? (
            <ResultViewMeasurementsParameterInput
              report={report}
              samplePoint={selectedSamplePoint}
              onRefresh={(sp) => {
                setSelectedSamplePoint(sp);
                onRefresh();
              }}
            />
          ) : (
            <ResultViewMeasurementsParameterView
              report={report}
              samplePoint={selectedSamplePoint}
              onRefresh={(sp) => {
                setSelectedSamplePoint(sp);
                onRefresh();
              }}
            />
          ))}

        {(userCategory === userCats.PERSONNEL || userCategory === userCats.REGULATOR) && (
          <ResultViewMeasurementsParameterView
            report={report}
            samplePoint={selectedSamplePoint}
            onRefresh={(sp) => {
              setSelectedSamplePoint(sp);
              onRefresh();
            }}
          />
        )}
      </Box>

      {/* MODALS */}
      {submitionDialog && (
        <SubmitResult
          open={submitionDialog}
          onClose={() => {
            setSubmitionDialog(false);
            onRefresh();
          }}
          reportId={report?.reportId}
          facilityId={report?.facilityId}
          reportQuarter={report?.reportQuarter}
          reportYear={report?.reportYear}
        />
      )}
      {openPublish && (
        <ApproveResult
          open={openPublish}
          title="Approve Result"
          onClose={() => {
            setOpenPublish(false);
            onRefresh();
          }}
          facilityId={report?.facilityId}
          reportQuarter={report?.reportQuarter}
          reportYear={report?.reportYear}
        />
      )}
      {openReject && (
        <RejectResult
          open={openReject}
          title="Reject Result"
          onClose={() => {
            setOpenReject(false);
            onRefresh();
          }}
          facilityId={report?.facilityId}
          reportQuarter={report?.reportQuarter}
          reportYear={report?.reportYear}
        />
      )}
      {openComment && (
        <ResultComments
          open={openComment}
          onClose={() => {
            setOpenComment(false);
            onRefresh();
          }}
          reportId={report?.reportId}
          facilityId={report?.facilityId}
          reportQuarter={report?.reportQuarter}
          reportYear={report?.reportYear}
          userId={userId}
        />
      )}
      {openFinalize && (
        <FinalizeResult
          open={openFinalize}
          onClose={() => {
            setOpenFinalize(false);
            onRefresh();
          }}
          reportId={report?.reportId}
          facilityId={report?.facilityId}
          reportQuarter={report?.reportQuarter}
          reportYear={report?.reportYear}
        />
      )}
    </Box>
  );
}
